import { FC, useEffect, useMemo, useState } from 'react';

import { useFormikContext } from 'formik';
import { compact, debounce } from 'lodash';
import { useGetAccountUsers } from 'modules/account/contact/hooks';
import { Button } from 'ui/components/common';
import {
  Currency,
  Input,
  Select,
  SelectAvatar,
  TagsField,
} from 'ui/components/form';
import { AvatarOption } from 'ui/components/form/SelectAvatar/SelectAvatar';
import { OPTIONS_PROPS } from 'ui/components/nav/Dropdown/Dropdown';
import useModal from 'ui/partials/Modal/useModal';
import { SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';
import { TOUR_SELECTOR } from 'ui/partials/Tour';

import { useGetPipelines, useGetStages } from '../hooks';
import { SIMPLE_DEAL_FORM } from './schema';

interface PROPS {
  isLoading: boolean;
  onCancel?: () => void;
}

const SimpleDealForm: FC<PROPS> = ({ isLoading, onCancel }) => {
  const {
    values,
    touched,
    errors,
    handleBlur,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    setFieldError,
    setErrors,
  } = useFormikContext<SIMPLE_DEAL_FORM>();

  //TODO: errors object in formik context is always empty. using this as temporary fix
  const isFormValid =
    values.name && values.pipelineId && values.stageId && values.type;

  const [accountUserKeyword, setAccountUserKeyword] = useState('');

  const { hideModal } = useModal();

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      hideModal();
    }
  };

  const { data: accountUsers } = useGetAccountUsers({
    keyword: accountUserKeyword,
  });

  const { data: pipelines } = useGetPipelines();
  const { data: stages } = useGetStages(values.pipelineId);

  const debouncedSearch = debounce((string, setter) => setter(string), 800);

  const accountUserItems = useMemo(
    () =>
      accountUsers?.data?.map((accountUser) => ({
        photoUrl: accountUser.photoUrl,
        itemLabel: accountUser.fullName,
        description: accountUser.emailAddress,
        value: accountUser._id,
      })) ?? ([] as AvatarOption[]),
    [accountUsers],
  );

  const loadAccountUserItems = (
    search = '',
    cb: (options: AvatarOption[]) => void,
  ) => {
    debouncedSearch(search, setAccountUserKeyword);

    cb(accountUserItems);
  };

  const defaultAccountUsers = useMemo(
    () =>
      accountUserItems?.filter((i) => values.accountUserId?.includes(i.value)),
    [accountUserItems, values.accountUserId],
  );

  useEffect(() => {
    if (stages?.length && !values.stageId) {
      setFieldValue('stageId', stages[0]?._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stages]);

  const stageOptions = useMemo(
    () =>
      stages?.map((stage) => ({
        value: stage?._id,
        label: stage.name,
      })) ?? [],
    [stages],
  );

  const pipelineOptions = useMemo(
    () =>
      pipelines?.map((pipeline) => ({
        value: pipeline?._id,
        label: pipeline.name,
      })) ?? [],
    [pipelines],
  );

  if (!values.stageId) {
    return <div />;
  }

  return (
    <form
      onSubmit={(e) => {
        handleSubmit(e);
      }}
    >
      <div>
        <div
          className="flex flex-col gap-y-4"
          data-tut={TOUR_SELECTOR.TOUR.SALES.DEAL.FORM.GENERAL.FORM}
        >
          <div>
            <Input
              className="w-full"
              type="text"
              name="name"
              size={SIZE.Small}
              textType={errors.name && touched.name ? 'danger' : 'default'}
              onChange={(e) => setFieldValue(e.target.name, e.target.value)}
              onBlur={handleBlur}
              value={values.name}
              placeholder="Enter Deal Name"
              subText={errors.name && touched.name ? errors.name : ''}
              label="Deal Name"
              disabled={isSubmitting}
              required
            />
          </div>

          <Select
            required
            label="Pipeline"
            value={
              pipelineOptions?.find(
                (pipeline) => pipeline.value === values.pipelineId,
              ) as unknown as string
            }
            onChange={(val: OPTIONS_PROPS) => {
              setFieldValue('pipelineId', val.value);
            }}
            placeholder="Select pipeline"
            className="flex-1"
            disabled={isSubmitting}
            name="pipelineId"
            options={pipelineOptions}
            textType={
              errors.pipelineId && touched.pipelineId ? 'danger' : 'default'
            }
            subText={
              errors.pipelineId && touched.pipelineId
                ? errors.pipelineId
                : undefined
            }
          />
          <Select
            required
            label="Stage"
            value={
              stageOptions?.find(
                (stage) => stage.value === values.stageId,
              ) as unknown as string
            }
            onChange={(val: OPTIONS_PROPS) => {
              setFieldValue('stageId', val.value);
            }}
            placeholder="Select stage"
            className="flex-1"
            disabled={isSubmitting}
            name="stageId"
            options={stageOptions}
            textType={errors.stageId && touched.stageId ? 'danger' : 'default'}
            subText={
              errors.stageId && touched.stageId ? errors.stageId : undefined
            }
          />

          <div className="flex gap-x-[34px]">
            <Currency
              className="flex-1"
              name="amount"
              size={SIZE.Small}
              onValueChange={(e) => setFieldValue('amount', e)}
              onBlur={handleBlur}
              value={values.amount ?? 0}
              placeholder="Input amount"
              subText={
                errors.amount && touched.amount ? errors.amount : undefined
              }
              textType={errors.amount && touched.amount ? 'danger' : 'default'}
              label="Amount"
              disabled={isSubmitting}
            />
          </div>
          <div>
            <Select
              className="w-full"
              textType={errors.type && touched.type ? 'danger' : 'default'}
              subText={errors.type && touched.type ? errors.type : undefined}
              label="Deal Type"
              onChange={(val) => setFieldValue('type', val.value)}
              options={[
                {
                  value: 'NEW_BUSINESS',
                  label: 'New Business',
                },
                {
                  value: 'EXISTING_BUSINESS',
                  label: 'Existing Business',
                },
              ]}
              size={SIZE.Small}
              name="type"
              placeholder="Select"
              disabled={isSubmitting}
              defaultValue={values.type ?? null}
              required
            />
          </div>
          <div>
            <SelectAvatar
              {...(errors.accountUserId && { state: 'danger' })}
              className="w-full"
              loadOptions={loadAccountUserItems}
              subText={
                errors.accountUserId && touched.accountUserId
                  ? errors.accountUserId
                  : undefined
              }
              label="Deal Owner"
              onChange={(val) => {
                setFieldValue(
                  'accountUserId',
                  (val as AvatarOption[]).map((v) => v.value),
                );
              }}
              onClear={() => setFieldValue('accountUserId', [])}
              size={SIZE.Small}
              name="accountUserId"
              isMulti
              placeholder="Select deal owner"
              defaultOptions
              defaultValue={defaultAccountUsers}
            />
          </div>
          <TagsField
            className="col-span-4 w-full"
            onChange={(val) =>
              setFieldValue(
                'tagId',
                val?.map((tag) => tag.value),
              )
            }
            defaultValue={compact(values.tagId)}
            setError={(err) => {
              if (err) {
                setFieldError(
                  'tagId',
                  'Tags cannot have spaces and special characters.',
                );
              } else {
                setErrors({});
              }
            }}
          />
        </div>
      </div>
      <div
        className={cn(
          'pt-[22px] mt-[22px]',
          'flex justify-end gap-2',
          'border-t-[0.5px] border-separator-primary',
        )}
      >
        <Button
          onClick={handleCancel}
          label="CANCEL"
          disabled={isSubmitting}
          intent="tertiary"
          size="sm"
        />
        <Button
          data-tut={TOUR_SELECTOR.TOUR.SALES.DEAL.FORM.GENERAL.SAVE_BUTTON}
          type="submit"
          label="SAVE"
          size="sm"
          disabled={!isFormValid || isSubmitting || isLoading}
        />
      </div>
    </form>
  );
};

export default SimpleDealForm;
